import {
  AlertOctagon,
  ArrowUp,
  BatteryWarning,
  Box,
  Camera,
  ChevronRight,
  CircleDollarSign,
  FileWarning,
  House,
  MapPin,
  PackageCheck,
  PackageOpen,
  Plus,
  Scale,
  StickyNote,
  ThermometerSnowflake,
  Truck,
  X
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useId, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import { ContentsEditor } from "../components/ContentsEditor";
import {
  CustomItemDetailsFields,
  customItemDetailsFromRecord,
  customItemPayload,
  type CustomItemDetailsValue
} from "../components/CustomItemDetailsFields";
import { dimensionsFromRecord } from "../components/DimensionCubeFields";
import {
  HandlingWarningFields,
  type HandlingWarningsValue
} from "../components/HandlingWarningFields";
import { NfcLinkPanel } from "../components/NfcLinkPanel";
import { PhotoUploader } from "../components/PhotoUploader";
import { Toast } from "../components/Toast";
import { ThermalPrinterPanel } from "../components/ThermalPrinterPanel";
import {
  ToteDetailsFields,
  toteDetailsFromRecord,
  toteDetailsPayload,
  type ToteDetailsValue
} from "../components/ToteDetailsFields";
import type { InventoryUnit, Location } from "../types";

export function InventoryDetailPage() {
  const { user } = useAuth();
  const { id = "" } = useParams();
  const [unit, setUnit] = useState<InventoryUnit | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [panel, setPanel] = useState<
    "photos" | "contents" | "location" | "note" | "tote-details" | "custom-details" | "warnings" | null
  >(null);
  const [quick, setQuick] = useState(localStorage.getItem("parallax-quick-mode") === "true");
  const [toast, setToast] = useState("");
  const [error, setError] = useState("");

  const reload = useCallback(() => {
    api.getInventory(id).then(setUnit).catch((reason) => setError(reason.message));
  }, [id]);

  useEffect(() => {
    reload();
    api.locations().then(setLocations);
  }, [reload]);

  async function status(next: string) {
    if (!unit) return;
    if (["Missing", "Damaged"].includes(next) && !window.confirm(`Report ${unit.stock_number} as ${next}?`)) {
      return;
    }
    try {
      const updated = await api.updateStatus(unit.id, next, unit.version);
      setUnit(updated);
      setToast(`Status changed to ${next}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Status update failed");
      reload();
    }
  }

  async function saveLocations(
    originRoom: string,
    currentLocationId: string,
    destinationRoomId: string
  ) {
    if (!unit) return;
    try {
      const updated = await api.updateInventory(unit.id, {
        version: unit.version,
        origin_room: originRoom || null,
        current_location_id: currentLocationId || null,
        destination_room_id: destinationRoomId || null
      });
      setUnit(updated);
      setPanel(null);
      setToast("Origin and destination saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Locations could not be saved");
      reload();
    }
  }

  async function saveNote(note: string) {
    if (!unit) return;
    try {
      setUnit(await api.updateInventory(unit.id, { version: unit.version, general_notes: note }));
      setPanel(null);
      setToast("Note saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Note update failed");
    }
  }

  async function saveToteDetails(details: ToteDetailsValue) {
    if (!unit) return;
    try {
      setUnit(
        await api.updateInventory(unit.id, {
          version: unit.version,
          ...toteDetailsPayload(details)
        })
      );
      setPanel(null);
      setToast("Tote details saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Tote details could not be saved");
    }
  }

  async function saveCustomDetails(details: CustomItemDetailsValue) {
    if (!unit) return;
    try {
      setUnit(await api.updateInventory(unit.id, { version: unit.version, ...customItemPayload(details) }));
      setPanel(null);
      setToast("Custom item details saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Custom details could not be saved");
    }
  }

  async function saveHandlingWarnings(warnings: HandlingWarningsValue) {
    if (!unit) return;
    try {
      setUnit(
        await api.updateInventory(unit.id, {
          version: unit.version,
          ...warnings
        })
      );
      setPanel(null);
      setToast("Handling warnings saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Handling warnings could not be saved");
    }
  }

  if (error && !unit) {
    return (
      <div className="page">
        <div className="alert error">{error}</div>
        <Link to="/inventory">Return to inventory</Link>
      </div>
    );
  }
  if (!unit) return <div className="loading-screen">Opening inventory…</div>;
  const mainPhoto =
    unit.photos.find((photo) => photo.category === "Container Exterior") || unit.photos[0];
  const nextAction = nextStatus(unit.move_status);
  const warnings: Array<{
    active: boolean;
    icon: LucideIcon;
    label: string;
    tone: string;
  }> = [
    { active: unit.fragile, icon: FileWarning, label: "Fragile", tone: "red" },
    { active: unit.heavy, icon: Scale, label: "Heavy", tone: "amber" },
    { active: unit.keep_upright, icon: ArrowUp, label: "Keep upright", tone: "red" },
    { active: unit.do_not_stack, icon: AlertOctagon, label: "Do not stack", tone: "red" },
    {
      active: unit.temperature_sensitive,
      icon: ThermometerSnowflake,
      label: "Temperature sensitive",
      tone: "blue"
    },
    {
      active: unit.lithium_batteries,
      icon: BatteryWarning,
      label: "Lithium batteries",
      tone: "amber"
    },
    { active: unit.high_value, icon: CircleDollarSign, label: "High value", tone: "amber" }
  ];

  return (
    <div className="record-page">
      <div className="record-heading">
        <div>
          <small>Stock number</small>
          <h1>{unit.stock_number}</h1>
          <span>Inventory name</span>
          <h2>{unit.name}</h2>
        </div>
        <label className="quick-toggle">
          <input
            type="checkbox"
            checked={quick}
            onChange={(event) => {
              setQuick(event.target.checked);
              localStorage.setItem("parallax-quick-mode", String(event.target.checked));
            }}
          />
          Quick update
        </label>
      </div>
      {mainPhoto ? (
        <img className="record-hero" src={mainPhoto.url} alt={`${unit.name} container`} />
      ) : (
        <button className="record-hero photo-placeholder" onClick={() => setPanel("photos")}>
          <Camera size={34} />
          <span>Add container photo</span>
        </button>
      )}
      <div className="record-facts">
        <button onClick={() => setPanel("location")}>
          <MapPin />
          <span>Current location</span>
          <strong>{unit.current_location?.name || "Not set"}</strong>
          <ChevronRight />
        </button>
        <button onClick={() => setPanel("location")}>
          <House />
          <span>Origin room</span>
          <strong>
            {unit.origin_room ||
              (unit.current_location?.group === "origin"
                ? unit.current_location.name
                : "Not set")}
          </strong>
          <ChevronRight />
        </button>
        <button onClick={() => setPanel("location")}>
          <Box />
          <span>Destination room</span>
          <strong>{unit.destination_room?.name || "Not set"}</strong>
          <ChevronRight />
        </button>
        <div>
          <PackageCheck />
          <span>Packing status</span>
          <strong className="status">{unit.packing_status}</strong>
        </div>
        <div>
          <Truck />
          <span>Move status</span>
          <strong className="status blue">{unit.move_status}</strong>
        </div>
      </div>
      <div className="warning-grid">
        {warnings
          .filter(({ active }) => active)
          .map(({ icon: Icon, label, tone }) => (
            <div className={`warning ${tone}`} key={label}>
              <Icon />
              <span>{label}</span>
            </div>
          ))}
      </div>
      {user?.role !== "viewer" && (
        <button className="record-note warning-edit-row" onClick={() => setPanel("warnings")}>
          <AlertOctagon />
          <span>
            <strong>Edit handling warnings</strong>
            <small>
              {warnings.some(({ active }) => active)
                ? warnings
                    .filter(({ active }) => active)
                    .map(({ label }) => label)
                    .join(" · ")
                : "No handling warnings selected"}
            </small>
          </span>
          <ChevronRight />
        </button>
      )}
      <button className="contents-summary" onClick={() => setPanel("contents")}>
        <PackageOpen />
        <div>
          <strong>Contents summary</strong>
          <span>
            {unit.items.length
              ? unit.items
                  .slice(0, 5)
                  .map((item) => `${item.item_name}${Number(item.quantity) > 1 ? ` ×${item.quantity}` : ""}`)
                  .join(", ")
              : "No contents recorded yet"}
          </span>
        </div>
        <small>{unit.items.length} items</small>
        <ChevronRight />
      </button>
      {unit.general_notes && (
        <button className="record-note" onClick={() => setPanel("note")}>
          <StickyNote />
          <span>{unit.general_notes}</span>
          <ChevronRight />
        </button>
      )}
      <div className="record-times">
        <span>Last updated</span>
        <strong>{formatTime(unit.updated_at)}</strong>
        <span>Last scanned</span>
        <strong>{unit.last_scanned_at ? formatTime(unit.last_scanned_at) : "Never"}</strong>
      </div>
      {unit.inventory_type === "TOTE" && user?.role !== "viewer" && (
        <button className="record-note tote-details-row" onClick={() => setPanel("tote-details")}>
          <Box />
          <span>
            <strong>{unit.container_type || "Choose tote type and size"}</strong>
            {(unit.container_size || unit.container_color || unit.lid_color) && (
              <small>
                {[unit.container_size, unit.container_color, unit.lid_color && `${unit.lid_color} lid`].filter(Boolean).join(" · ")}
              </small>
            )}
          </span>
          <ChevronRight />
        </button>
      )}
      {unit.inventory_type !== "TOTE" && user?.role !== "viewer" && (
        <button className="record-note tote-details-row" onClick={() => setPanel("custom-details")}>
          <Box />
          <span>
            <strong>{unit.container_type || "Add custom item size and color"}</strong>
            <small>{customSummary(unit) || "Dimensions and color not set"}</small>
          </span>
          <ChevronRight />
        </button>
      )}
      <ThermalPrinterPanel
        unitId={unit.id}
        stockNumber={unit.stock_number}
        canPrint={user?.role !== "viewer"}
        onSuccess={setToast}
        onError={setError}
      />
      {user?.role === "administrator" && <NfcLinkPanel unitId={unit.id} onChanged={reload} />}
      {!quick && (
        <details className="secondary-actions">
          <summary>More move actions</summary>
          <div>
            {["Packed", "Staged", "Loaded", "Unloaded", "Delivered", "Verified"].map((value) => (
              <button className="button secondary" onClick={() => status(value)} key={value}>
                Mark {value}
              </button>
            ))}
            <button className="button warning-button" onClick={() => status("Missing")}>
              Report missing
            </button>
            <button className="button danger-button" onClick={() => status("Damaged")}>
              Report damage
            </button>
          </div>
        </details>
      )}
      <div className="mobile-action-dock">
        <button onClick={() => setPanel("photos")}>
          <Camera />
          Take photo
        </button>
        <button onClick={() => setPanel("contents")}>
          <Plus />
          Add item
        </button>
        <button onClick={() => setPanel("location")}>
          <MapPin />
          Change location
        </button>
        <button onClick={() => setPanel("note")}>
          <StickyNote />
          Add note
        </button>
        {nextAction && (
          <button className="primary-action" onClick={() => status(nextAction)}>
            <Truck />
            Mark as {nextAction}
          </button>
        )}
      </div>
      {panel === "contents" && (
        <ContentsEditor
          unitId={unit.id}
          items={unit.items}
          onChanged={reload}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === "photos" && (
        <Sheet title="Add photographs" onClose={() => setPanel(null)}>
          <PhotoUploader
            unitId={unit.id}
            onComplete={() => {
              reload();
              setPanel(null);
              setToast("Photos uploaded");
            }}
          />
        </Sheet>
      )}
      {panel === "location" && (
        <LocationSheet
          unit={unit}
          locations={locations}
          onSave={saveLocations}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === "note" && (
        <NoteSheet initial={unit.general_notes || ""} onSave={saveNote} onClose={() => setPanel(null)} />
      )}
      {panel === "tote-details" && (
        <ToteDetailsSheet unit={unit} onSave={saveToteDetails} onClose={() => setPanel(null)} />
      )}
      {panel === "custom-details" && (
        <CustomDetailsSheet unit={unit} onSave={saveCustomDetails} onClose={() => setPanel(null)} />
      )}
      {panel === "warnings" && (
        <HandlingWarningsSheet
          unit={unit}
          onSave={saveHandlingWarnings}
          onClose={() => setPanel(null)}
        />
      )}
      {toast && <Toast message={toast} tone="success" onClose={() => setToast("")} />}
      {error && <Toast message={error} tone="error" onClose={() => setError("")} />}
    </div>
  );
}

function LocationSheet({
  unit,
  locations,
  onSave,
  onClose
}: {
  unit: InventoryUnit;
  locations: Location[];
  onSave: (originRoom: string, currentLocationId: string, destinationRoomId: string) => void;
  onClose: () => void;
}) {
  const inferredOrigin =
    unit.origin_room ||
    (unit.current_location?.group === "origin" ? unit.current_location.name : "");
  const [originRoom, setOriginRoom] = useState(inferredOrigin);
  const [currentLocationId, setCurrentLocationId] = useState(unit.current_location_id || "");
  const [destinationRoomId, setDestinationRoomId] = useState(
    unit.destination_room_id || ""
  );

  function changeOrigin(nextOrigin: string) {
    const current = locations.find((location) => location.id === currentLocationId);
    setOriginRoom(nextOrigin);
    if (!currentLocationId || current?.group === "origin") {
      const matchingLocation = locations.find(
        (location) => location.group === "origin" && location.name === nextOrigin
      );
      setCurrentLocationId(matchingLocation?.id || "");
    }
  }

  return (
    <Sheet title="Edit tote locations" onClose={onClose}>
      <div className="location-editor">
        <p>
          Origin stays with the tote’s history. Current location changes during the move.
          Destination is where the tote should be delivered.
        </p>
        <label>
          Origin room
          <select value={originRoom} onChange={(event) => changeOrigin(event.target.value)}>
            <option value="">Not set</option>
            {locations
              .filter((location) => location.group === "origin")
              .map((location) => (
                <option key={location.id} value={location.name}>
                  {location.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          Current location
          <select
            value={currentLocationId}
            onChange={(event) => setCurrentLocationId(event.target.value)}
          >
            <option value="">Not set</option>
            {(["origin", "transit", "destination"] as const).map((group) => (
              <optgroup label={group[0].toUpperCase() + group.slice(1)} key={group}>
                {locations
                  .filter((location) => location.group === group)
                  .map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </label>
        <label>
          Destination room
          <select
            value={destinationRoomId}
            onChange={(event) => setDestinationRoomId(event.target.value)}
          >
            <option value="">Not set</option>
            {locations
              .filter((location) => location.group === "destination")
              .map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
          </select>
        </label>
        <button
          className="button primary full"
          onClick={() => onSave(originRoom, currentLocationId, destinationRoomId)}
        >
          Save origin and destination
        </button>
      </div>
    </Sheet>
  );
}

function HandlingWarningsSheet({
  unit,
  onSave,
  onClose
}: {
  unit: InventoryUnit;
  onSave: (value: HandlingWarningsValue) => void;
  onClose: () => void;
}) {
  const [warnings, setWarnings] = useState<HandlingWarningsValue>({
    fragile: unit.fragile,
    heavy: unit.heavy,
    keep_upright: unit.keep_upright,
    do_not_stack: unit.do_not_stack,
    temperature_sensitive: unit.temperature_sensitive,
    lithium_batteries: unit.lithium_batteries
  });
  return (
    <Sheet title="Handling warnings" onClose={onClose}>
      <HandlingWarningFields
        value={warnings}
        onChange={(field, checked) => setWarnings({ ...warnings, [field]: checked })}
      />
      <button className="button primary full" onClick={() => onSave(warnings)}>
        Save handling warnings
      </button>
    </Sheet>
  );
}

function ToteDetailsSheet({
  unit,
  onSave,
  onClose
}: {
  unit: InventoryUnit;
  onSave: (value: ToteDetailsValue) => void;
  onClose: () => void;
}) {
  const [details, setDetails] = useState(() =>
    toteDetailsFromRecord(
      unit.container_type,
      unit.container_size,
      unit.container_color,
      unit.lid_color,
      dimensionsFromRecord(unit.length, unit.width, unit.height)
    )
  );
  return (
    <Sheet title="Tote details" onClose={onClose}>
      <ToteDetailsFields value={details} onChange={setDetails} />
      <button className="button primary full" onClick={() => onSave(details)}>
        Save tote details
      </button>
    </Sheet>
  );
}

function CustomDetailsSheet({ unit, onSave, onClose }: {
  unit: InventoryUnit;
  onSave: (value: CustomItemDetailsValue) => void;
  onClose: () => void;
}) {
  const [details, setDetails] = useState(() => customItemDetailsFromRecord(
    unit.container_type,
    unit.container_color,
    dimensionsFromRecord(unit.length, unit.width, unit.height),
    unit.inventory_type
  ));
  return (
    <Sheet title="Custom item details" onClose={onClose}>
      <CustomItemDetailsFields value={details} onChange={setDetails} />
      <button className="button primary full" onClick={() => onSave(details)}>Save custom details</button>
    </Sheet>
  );
}

function customSummary(unit: InventoryUnit) {
  const dimensions = [unit.length, unit.width, unit.height].some(Boolean)
    ? `${unit.length || "—"} × ${unit.width || "—"} × ${unit.height || "—"} in`
    : "";
  return [dimensions, unit.container_color].filter(Boolean).join(" · ");
}

function nextStatus(current: string) {
  const order = ["Draft", "Cataloged", "Packing", "Packed", "Staged", "Loaded", "Unloaded", "Delivered", "Verified"];
  const index = order.indexOf(current);
  return index >= 0 && index < order.length - 1 ? order[index + 1] : null;
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function Sheet({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const titleId = useId();
  return (
    <div className="modal-backdrop">
      <section
        className="modal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header>
          <h2 id={titleId}>{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function NoteSheet({
  initial,
  onSave,
  onClose
}: {
  initial: string;
  onSave: (value: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState(initial);
  return (
    <Sheet title="Inventory note" onClose={onClose}>
      <label>
        Note
        <textarea rows={8} value={note} onChange={(event) => setNote(event.target.value)} autoFocus />
      </label>
      <button className="button primary full" onClick={() => onSave(note)}>
        Save note
      </button>
    </Sheet>
  );
}
